/* Innovation Flooring Direct — carpet quote engine.
   Pure calculation only. No DOM, no fetch, no globals other than CQEngine.
   All lengths are handled in whole centimetres inside this file and returned
   in metres. All money is in pence.

   Rules implemented (approved build spec, 7 Sept 2026):
   - 0.10m trimming allowance added ONCE to the finished overall length and
     ONCE to the finished overall width of each area.
   - Linear cuts round UP to the next 0.10m.
   - Plain carpet may be turned; directional, patterned and tartan may not.
   - Chargeable carpet area is always roll width x linear cut.
*/
(function (root, factory) {
  var CQ = factory();
  if (typeof module === 'object' && module.exports) { module.exports = CQ; }
  if (root) { root.CQEngine = CQ; }
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ---------------------------------------------------------------- units */
  var TRIM = 10;         /* cm added once per finished dimension            */
  var STAIR_ALLOW = 30;  /* cm added once to each stair strip               */
  var GRIP = 152;        /* cm per gripper length                           */
  var DOORWAY = 80;      /* cm deducted from a perimeter per doorway        */

  var RATES = {
    underlay10: 599, underlay12: 699,
    gripper: 100, doorbar: 999,
    fitting: 500, fitMinRooms: 12000, fitMinStairs: 15000,
    uplift: 150, disposal: 250, furniture: 3000,
    delivery: 3000, deposit: 5000
  };

  function cm(v) { var n = Number(v); if (!isFinite(n)) n = 0; return Math.round(n * 100); }
  function toM(c) { return Math.round(c) / 100; }
  function up10(c) { c = Math.round(c); return Math.ceil(c / 10) * 10; }
  function m2(cm2) { return Math.round(cm2) / 10000; }
  function r2(x) { return Math.round(x * 100) / 100; }
  /* Fitted areas are kept to the millimetre-square so a stair area like
     7.665 m2 is not rounded before it is multiplied by a rate. */
  function r3(x) { return Math.round(x * 1000) / 1000; }

  /* Metres, centimetres or feet and inches, converted on entry. */
  function toMetres(value, unit, inches) {
    var v = Number(value); if (!isFinite(v)) v = 0;
    var i = Number(inches); if (!isFinite(i)) i = 0;
    if (unit === 'cm') return r2(v / 100);
    if (unit === 'ft') return r2(((v * 12) + i) * 0.0254);
    return r2(v);
  }

  function isPlain(cls) { return /^plain/i.test(String(cls || '')); }
  function isPatterned(cls) { return /pattern|tartan|woven/i.test(String(cls || '')); }
  /* A missing classification is treated as directional, which is the safe
     assumption: the cut is never turned. */
  function canTurn(cls) { return isPlain(cls); }

  function fmtM(x) { return (Math.round(x * 100) / 100).toFixed(2); }

  /* ---------------------------------------------------------------- rooms */
  /* room = { name, length, width, doorways, extras:[{depth,width,side}] }
     side 'width'  = the alcove or bay makes the room wider
     side 'length' = the alcove or bay makes the room longer
     The cut is taken from the overall bounding box so the carpet always
     covers; the floor area is the sum of the rectangles. */
  function roomGeom(room) {
    var L = cm(room.length), W = cm(room.width);
    var boxL = L, boxW = W, floor = L * W, per = 2 * (L + W);
    var extras = room.extras || [];
    for (var i = 0; i < extras.length; i++) {
      var d = cm(extras[i].depth), w = cm(extras[i].width);
      if (d <= 0 || w <= 0) continue;
      floor += d * w;
      per += 2 * d;                       /* the extra rectangle adds two returns */
      if (extras[i].side === 'length') { boxL += d; } else { boxW += d; }
    }
    return { Lcm: L, Wcm: W, boxLcm: boxL, boxWcm: boxW, floorCm2: floor, perimCm: per };
  }

  function option(orient, downCm, acrossCm, rollCm, drops, extraCm) {
    var one = up10(downCm);
    var cut = drops === 2 ? one + up10(downCm + (extraCm || 0)) : one;
    return {
      orientation: orient,
      drops: drops,
      join: drops > 1,
      downCm: downCm,
      acrossCm: acrossCm,
      cutCm: cut,
      chargeCm2: rollCm * cut
    };
  }

  /* roomCut(room, rollWidthM, classification, patternRepeatM) */
  function roomCut(room, rollWidthM, cls, patternRepeatM) {
    var g = roomGeom(room);
    var R = cm(rollWidthM);
    var A = g.boxLcm + TRIM;   /* down the roll in orientation A */
    var B = g.boxWcm + TRIM;   /* across the roll in orientation A */
    var turn = canTurn(cls);
    var rep = isPatterned(cls) ? cm(patternRepeatM) : 0;

    if (!R || A <= TRIM || B <= TRIM) {
      return { ok: false, blocked: true, reason: 'missing', message: 'Please add the room length and width.' };
    }
    if (isPatterned(cls) && !rep) {
      return {
        ok: false, blocked: true, reason: 'pattern',
        message: 'This carpet has a pattern that has to be matched, and we do not have its pattern repeat on file. Please call the shop and we will quote it by hand.'
      };
    }

    var singles = [];
    if (B <= R) singles.push(option('A', A, B, R, 1, 0));
    if (turn && A <= R) singles.push(option('B', B, A, R, 1, 0));

    var joins = [];
    if (B <= 2 * R) joins.push(option('A', A, B, R, 2, rep));
    if (turn && A <= 2 * R) joins.push(option('B', B, A, R, 2, rep));

    var pool = singles.length ? singles : joins;
    if (!pool.length) {
      return {
        ok: false, blocked: true, reason: 'toowide',
        message: 'This area is wider than two widths of this carpet, so we cannot work the cut out online. Please call the shop and we will measure and quote it by hand.'
      };
    }
    /* Least carpet used wins. A tie goes to orientation A. */
    var best = pool[0];
    for (var i = 1; i < pool.length; i++) { if (pool[i].chargeCm2 < best.chargeCm2) best = pool[i]; }

    var acrossUsed = best.acrossCm;
    var offW = (best.drops === 2 ? 2 * R : R) - acrossUsed;
    var offL = best.drops === 2 ? up10(best.downCm) : best.cutCm;

    var res = {
      ok: true,
      kind: 'room',
      name: room.name || 'Room',
      orientation: best.orientation,
      turned: best.orientation === 'B',
      join: best.join,
      drops: best.drops,
      rollWidthM: toM(R),
      cutM: toM(best.cutCm),
      cutCm: best.cutCm,
      units: best.cutCm / 10,
      acrossM: toM(acrossUsed),
      downM: toM(best.downCm),
      chargeableM2: m2(best.chargeCm2),
      floorM2: m2(g.floorCm2),
      roomLM: toM(g.Lcm), roomWM: toM(g.Wcm),
      boxLM: toM(g.boxLcm), boxWM: toM(g.boxWcm),
      perimeterM: toM(g.perimCm),
      doorways: Number(room.doorways) || 0,
      offcut: { widthM: toM(offW), lengthM: toM(offL) },
      patternRepeatM: rep ? toM(rep) : 0
    };
    res.pieces = piecesForRoom(res, R);
    res.shelves = roomShelves(res, R, best);
    res.sentence = roomSentence(res);
    res.why = roomWhy(res);
    return res;
  }

  /* How the cut sits on the roll, for the layout drawing and the cutting plan. */
  function roomShelves(res, R, best) {
    var across = cm(res.acrossM);
    if (best.drops === 1) {
      return [{
        yCm: 0, cutCm: best.cutCm, usedCm: across,
        items: [{ label: res.name, kind: 'room', xCm: 0, acrossCm: across, downCm: best.cutCm }],
        spareAcrossCm: R - across, spareDownCm: 0
      }];
    }
    var one = up10(best.downCm);
    var two = best.cutCm - one;
    var first = Math.min(R, across), second = across - first;
    return [
      { yCm: 0, cutCm: one, usedCm: first,
        items: [{ label: res.name + ' (first width)', kind: 'room', xCm: 0, acrossCm: first, downCm: one }],
        spareAcrossCm: R - first, spareDownCm: 0 },
      { yCm: one, cutCm: two, usedCm: second,
        items: [{ label: res.name + ' (second width, joined)', kind: 'room', xCm: 0, acrossCm: second, downCm: two }],
        spareAcrossCm: R - second, spareDownCm: 0 }
    ];
  }

  function piecesForRoom(res, R) {
    var out = [];
    if (res.drops === 2) {
      var need = cm(res.acrossM);
      var first = Math.min(R, need);
      out.push({ label: res.name + ' (first width)', acrossCm: first, downCm: up10(cm(res.downM)) });
      out.push({ label: res.name + ' (second width, joined)', acrossCm: need - first, downCm: up10(cm(res.downM)) });
    } else {
      out.push({ label: res.name, acrossCm: cm(res.acrossM), downCm: res.cutCm });
    }
    return out;
  }

  function roomSentence(r) {
    return 'Your room measures ' + fmtM(r.roomLM) + 'm x ' + fmtM(r.roomWM) + 'm. Using this '
      + fmtM(r.rollWidthM) + 'm-wide carpet, we recommend a ' + fmtM(r.cutM)
      + '-linear-metre cut. You will receive ' + fmtM(r.rollWidthM) + 'm x ' + fmtM(r.cutM)
      + 'm = ' + fmtM(r.chargeableM2) + 'm².';
  }

  function roomWhy(r) {
    if (r.join) {
      return 'Your room is wider than the roll, so the carpet comes in two widths laid the same way and joined. '
        + 'The cut is bigger than the floor because carpet is sold in full widths off the roll, and we add 0.10m to the length and 0.10m to the width for trimming in.';
    }
    return 'The cut is bigger than your floor because carpet comes in one full width off the roll, and we add 0.10m to the length and 0.10m to the width so it can be trimmed in neatly at the skirting and the door.';
  }

  /* ------------------------------------------------------------ cut to length */
  /* Round 5, 15 Sept 2026. The customer knows the length they need and buys
     it straight off the roll: any length from 0.50m up, rounded UP to the
     next 10cm, at the roll's own width. NO trimming allowance is added: they
     asked for a length, so they get exactly that length. The cut is never
     turned and never joined, so the pattern or pile classification does not
     change it; a patterned carpet with a repeat on file is simply cut on the
     repeat where the customer asks.
     area = { kind: 'cut', name, lengthM } */
  var CUT_MIN = 50;      /* cm: the shortest cut we make                    */

  /* "4m", "5m", "3.66m": the roll width as it is said in the shop. */
  function fmtW(x) { var s = (Math.round(x * 100) / 100).toFixed(2); return s.replace(/\.?0+$/, ''); }

  function lengthCut(area, rollWidthM, cls, patternRepeatM) {
    var R = cm(rollWidthM);
    var want = cm(area.lengthM);
    if (!R || want <= 0) {
      return { ok: false, blocked: true, reason: 'missing', message: 'Please tell us the length you need.' };
    }
    if (want < CUT_MIN) {
      return { ok: false, blocked: true, reason: 'short', message: 'The shortest cut we make is 0.50m. Please enter 0.50m or more.' };
    }
    var cut = up10(want);
    var rep = isPatterned(cls) ? cm(patternRepeatM) : 0;
    var name = area.name || 'Cut to length';
    var res = {
      ok: true,
      kind: 'cut',
      name: name,
      orientation: 'A',
      turned: false,
      join: false,
      drops: 1,
      rollWidthM: toM(R),
      requestedM: toM(want),
      rounded: cut !== want,
      cutM: toM(cut),
      cutCm: cut,
      units: cut / 10,
      acrossM: toM(R),
      downM: toM(cut),
      chargeableM2: m2(R * cut),
      floorM2: m2(R * cut),                 /* the whole cut is theirs */
      perimeterM: toM(2 * (R + cut)),       /* only used if they ask for gripper */
      doorways: Number(area.doorways) || 0,
      offcut: { widthM: 0, lengthM: 0 },
      patternRepeatM: rep ? toM(rep) : 0,
      pieces: [{ label: name, acrossCm: R, downCm: cut }],
      shelves: [{
        yCm: 0, cutCm: cut, usedCm: R,
        items: [{ label: name, kind: 'cut', xCm: 0, acrossCm: R, downCm: cut }],
        spareAcrossCm: 0, spareDownCm: 0
      }]
    };
    res.sentence = cutSentence(res);
    res.why = cutWhy(res);
    return res;
  }

  function cutSentence(r) {
    var s = fmtM(r.cutM) + 'm from the ' + fmtW(r.rollWidthM) + 'm roll: ' + fmtM(r.chargeableM2)
      + ' m² of carpet, cut straight off the roll, no join. No trimming allowance is added, you get exactly the length you asked for.';
    if (r.patternRepeatM) s += ' This carpet has a ' + fmtM(r.patternRepeatM) + 'm pattern repeat, and we cut on the repeat where you ask us to.';
    return s;
  }

  function cutWhy(r) {
    if (r.rounded) {
      return 'We cut in 10cm steps, so your ' + fmtM(r.requestedM) + 'm becomes ' + fmtM(r.cutM) + 'm. The carpet comes in the full width of the roll, so the price is the roll width times the length.';
    }
    return 'The carpet comes in the full width of the roll, so the price is the roll width times the length.';
  }

  /* --------------------------------------------------------------- stairs */
  /* stairs = {
       type: 'straight'|'quarter'|'half'|'winding',
       flights: [{ label, count, tread, riser }],
       coverage: 'full'|'runner', stairWidth, runnerWidth,
       landings: [{ name, length, width, doorways }],
       winders: [{ count, maxTread, riser, width }]
     } */
  function stripWidths(stairs) {
    var finished, cut;
    if (stairs.coverage === 'runner') {
      finished = cm(stairs.runnerWidth || 0.69);
      cut = finished + TRIM;
    } else {
      finished = cm(stairs.stairWidth || 0);
      cut = finished + TRIM;
    }
    return { finishedCm: finished, cutCm: cut };
  }

  function stairsPieces(stairs) {
    var w = stripWidths(stairs);
    var pieces = [], strips = [], i;
    var flights = stairs.flights || [];
    for (i = 0; i < flights.length; i++) {
      var f = flights[i];
      var n = Number(f.count) || 0;
      var per = cm(f.tread) + cm(f.riser);
      if (n <= 0 || per <= 0) continue;
      var len = (n * per) + STAIR_ALLOW;
      var s = {
        label: f.label || ('Stair strip ' + (i + 1)),
        stairs: n,
        treadM: toM(cm(f.tread)), riserM: toM(cm(f.riser)),
        lengthM: toM(len), lengthCm: len,
        finishedWidthM: toM(w.finishedCm), cutWidthM: toM(w.cutCm),
        /* stripAreaM2 is the piece of carpet cut from the roll, allowance and
           all. fittedAreaM2 is the real surface the fitter covers, tread plus
           riser on every step at the finished width, with no allowance: that is
           what fitting, uplift and disposal are charged on. */
        stripAreaM2: m2(len * w.cutCm),
        fittedAreaM2: m2(n * per * w.finishedCm),
        areaM2: m2(len * w.cutCm)
      };
      strips.push(s);
      pieces.push({ label: s.label, acrossCm: w.cutCm, downCm: len, kind: 'strip', fixed: true });
    }
    /* Winder treads: each one is its own piece, measured at its widest point. */
    var winders = [], wd = stairs.winders || [];
    for (i = 0; i < wd.length; i++) {
      var g = wd[i];
      var c = Number(g.count) || 0;
      var down = cm(g.maxTread) + cm(g.riser) + TRIM;
      var across = cm(g.width) + TRIM;
      if (c <= 0 || down <= TRIM || across <= TRIM) continue;
      var fitDown = cm(g.maxTread) + cm(g.riser), fitAcross = cm(g.width);
      for (var k = 0; k < c; k++) {
        pieces.push({ label: 'Winder tread ' + (winders.length + 1), acrossCm: across, downCm: down, kind: 'winder', fixed: true });
        winders.push({
          label: 'Winder tread ' + winders.length,
          downM: toM(down), acrossM: toM(across),
          stripAreaM2: m2(down * across),
          fittedAreaM2: m2(fitDown * fitAcross),
          areaM2: m2(down * across)
        });
      }
    }
    return { pieces: pieces, strips: strips, winders: winders, widths: w };
  }

  function landingPieces(landings, turn) {
    var out = [], info = [];
    var list = landings || [];
    for (var i = 0; i < list.length; i++) {
      var l = list[i];
      var L = cm(l.length), W = cm(l.width);
      if (L <= 0 || W <= 0) continue;
      var name = l.name || ('Landing ' + (i + 1));
      var a = { acrossCm: W + TRIM, downCm: L + TRIM };
      var b = { acrossCm: L + TRIM, downCm: W + TRIM };
      out.push({
        label: name, kind: 'landing',
        acrossCm: a.acrossCm, downCm: a.downCm,
        alt: turn ? b : null
      });
      info.push({
        name: name, lengthM: toM(L), widthM: toM(W),
        cutLengthM: toM(L + TRIM), cutWidthM: toM(W + TRIM),
        floorM2: m2(L * W), perimeterM: toM(2 * (L + W)),
        doorways: Number(l.doorways) || 0
      });
    }
    return { pieces: out, info: info };
  }

  /* Shelf packing down the roll. Pieces are laid in order; a piece sits beside
     the previous ones when it fits inside the roll width, otherwise it starts
     a new length further down the roll. */
  function layout(pieces, rollWidthM) {
    var R = cm(rollWidthM);
    var shelves = [];
    var open = null;
    for (var i = 0; i < pieces.length; i++) {
      var p = pieces[i];
      var use = { acrossCm: p.acrossCm, downCm: p.downCm };
      if (p.alt && open) {
        /* Prefer whichever way round fits beside what is already down. */
        var fitsA = open.usedCm + use.acrossCm <= R;
        var fitsB = open.usedCm + p.alt.acrossCm <= R;
        if (!fitsA && fitsB) { use = p.alt; }
        else if (fitsA && fitsB && p.alt.acrossCm < use.acrossCm) { use = p.alt; }
      } else if (p.alt && !open && p.alt.acrossCm < use.acrossCm && p.alt.acrossCm <= R) {
        use = p.alt;
      }
      if (use.acrossCm > R) {
        return { ok: false, blocked: true, reason: 'toowide', piece: p.label,
          message: p.label + ' is wider than this roll, so we cannot work it out online. Please call the shop and we will quote it by hand.' };
      }
      if (!open || open.usedCm + use.acrossCm > R) {
        open = { usedCm: 0, downCm: 0, items: [] };
        shelves.push(open);
      }
      open.items.push({ label: p.label, kind: p.kind || 'piece', xCm: open.usedCm, acrossCm: use.acrossCm, downCm: use.downCm });
      open.usedCm += use.acrossCm;
      if (use.downCm > open.downCm) open.downCm = use.downCm;
    }
    var total = 0, y = 0, out = [];
    for (var s = 0; s < shelves.length; s++) {
      var sh = shelves[s];
      var cut = up10(sh.downCm);
      out.push({
        yCm: y, cutCm: cut, usedCm: sh.usedCm, items: sh.items,
        spareAcrossCm: R - sh.usedCm, spareDownCm: cut - sh.downCm
      });
      y += cut; total += cut;
    }
    return { ok: true, rollWidthCm: R, rollWidthM: toM(R), shelves: out, cutCm: total, cutM: toM(total), chargeableM2: m2(R * total) };
  }

  /* stairsCut(stairs, rollWidthM, classification) */
  function stairsCut(stairs, rollWidthM, cls) {
    var sp = stairsPieces(stairs);
    var lp = landingPieces(stairs.landings, canTurn(cls));
    var pieces = sp.pieces.concat(lp.pieces);
    if (!pieces.length) {
      return { ok: false, blocked: true, reason: 'missing', message: 'Please add your stair measurements.' };
    }
    var lay = layout(pieces, rollWidthM);
    if (!lay.ok) return { ok: false, blocked: true, reason: lay.reason, message: lay.message };

    /* floor = the real area a fitter covers, with no cutting allowance in it.
       carpet = the pieces cut from the roll, allowance included, for display. */
    var floor = 0, carpet = 0, i;
    for (i = 0; i < sp.strips.length; i++) { floor += sp.strips[i].fittedAreaM2; carpet += sp.strips[i].stripAreaM2; }
    for (i = 0; i < sp.winders.length; i++) { floor += sp.winders[i].fittedAreaM2; carpet += sp.winders[i].stripAreaM2; }
    for (i = 0; i < lp.info.length; i++) { floor += lp.info[i].floorM2; carpet += lp.info[i].cutLengthM * lp.info[i].cutWidthM; }

    var res = {
      ok: true, kind: 'stairs',
      type: stairs.type || 'straight',
      estimate: (stairs.type === 'winding') || sp.winders.length > 0,
      coverage: stairs.coverage || 'full',
      stairWidthM: toM(cm(stairs.stairWidth || 0)),
      finishedWidthM: toM(sp.widths.finishedCm),
      cutWidthM: toM(sp.widths.cutCm),
      strips: sp.strips, winders: sp.winders, landings: lp.info,
      rollWidthM: lay.rollWidthM,
      layout: lay,
      cutM: lay.cutM, cutCm: lay.cutCm, units: lay.cutCm / 10,
      chargeableM2: lay.chargeableM2,
      floorM2: r3(floor),
      cutPiecesM2: r2(carpet),
      stairCount: sp.strips.reduce(function (a, b) { return a + b.stairs; }, 0),
      shelves: lay.shelves,
      disclaimer: DISCLAIMER
    };
    res.sentence = stairSentence(res);
    return res;
  }

  var DISCLAIMER = 'Recommended quantity only. Stair shapes, pile direction, pattern matching and fitting conditions can affect the final cut. We will confirm your measurements before your carpet is cut.';

  function stairSentence(r) {
    var s = 'Based on your ' + r.stairCount + ' stairs';
    if (r.landings.length) s += ' and landing measurements';
    s += ', we recommend a ' + fmtM(r.rollWidthM) + 'm-wide carpet cut of approximately '
      + fmtM(r.cutM) + ' linear metres, giving a total of ' + fmtM(r.chargeableM2) + 'm².';
    return s;
  }

  function widthLabel(r) {
    if (r.coverage === 'runner') {
      return 'Finished runner width: ' + fmtM(r.finishedWidthM) + 'm. Cut from the roll at '
        + fmtM(r.cutWidthM) + 'm, then bound or edged to the finished width.';
    }
    return 'Full-width stairs. Your stairs measure ' + fmtM(r.stairWidthM)
      + 'm across, and we cut from the roll at ' + fmtM(r.cutWidthM) + 'm so it can be trimmed in at both sides.';
  }

  /* ---------------------------------------------------------- accessories */
  function accessories(opts) {
    var rates = opts.rates || RATES;
    var floorM2 = opts.floorM2 || 0;
    var out = {};

    /* Underlay: floor area plus 5%, rounded up to the next whole m2. */
    var uM2 = Math.ceil((floorM2 * 1.05) - 0.00001);
    var uRate = opts.underlay === '10mm' ? rates.underlay10 : (opts.underlay === '12mm' ? rates.underlay12 : 0);
    out.underlay = {
      choice: opts.underlay || 'none',
      m2: (opts.underlay === '10mm' || opts.underlay === '12mm') ? uM2 : 0,
      ratePence: uRate,
      pence: (opts.underlay === '10mm' || opts.underlay === '12mm') ? uM2 * uRate : 0
    };

    /* Gripper: 1.52m lengths. */
    var gripCm = 0;
    var rooms = opts.rooms || [];
    for (var i = 0; i < rooms.length; i++) {
      gripCm += cm(rooms[i].perimeterM) - (DOORWAY * (rooms[i].doorways || 0));
    }
    var lands = opts.landings || [];
    for (i = 0; i < lands.length; i++) {
      gripCm += cm(lands[i].perimeterM) - (DOORWAY * (lands[i].doorways || 0));
    }
    gripCm += (opts.stairCount || 0) * 2 * cm(opts.stairWidthM || 0);
    if (gripCm < 0) gripCm = 0;
    var lengths = opts.gripper === 'new' ? Math.ceil(gripCm / GRIP - 0.00001) : 0;
    out.gripper = {
      choice: opts.gripper || 'none',
      metres: toM(gripCm),
      lengths: lengths,
      ratePence: rates.gripper,
      pence: lengths * rates.gripper
    };

    var bars = Number(opts.doorBars) || 0;
    out.doorBars = { count: bars, ratePence: rates.doorbar, pence: bars * rates.doorbar };

    out.uplift = { on: !!opts.uplift, m2: floorM2, ratePence: rates.uplift, pence: opts.uplift ? Math.round(floorM2 * rates.uplift) : 0 };
    out.disposal = { on: !!opts.disposal, m2: floorM2, ratePence: rates.disposal, pence: opts.disposal ? Math.round(floorM2 * rates.disposal) : 0 };
    var fr = Number(opts.furnitureRooms) || 0;
    out.furniture = { rooms: fr, ratePence: rates.furniture, pence: fr * rates.furniture };
    return out;
  }

  /* Fitting is charged on the floor area actually fitted, with a minimum.
     The higher of the two is charged, never both. */
  function fitting(floorM2, hasStairs, wanted, rates) {
    rates = rates || RATES;
    if (!wanted) return { wanted: false, pence: 0, calcPence: 0, minPence: 0, minApplied: false, m2: floorM2 };
    var calc = Math.round(floorM2 * rates.fitting);
    var min = hasStairs ? rates.fitMinStairs : rates.fitMinRooms;
    var use = Math.max(calc, min);
    return {
      wanted: true, m2: r3(floorM2), ratePence: rates.fitting,
      calcPence: calc, minPence: min, minApplied: use === min && calc < min, pence: use, hasStairs: !!hasStairs
    };
  }

  function delivery(route, fittingWanted, rates) {
    rates = rates || RATES;
    if (route === 'collect') return { route: 'collect', pence: 0, label: 'Collection from our Clydebank store', free: true };
    if (fittingWanted) return { route: 'delivery', pence: 0, label: 'Delivered free with your fitting', free: true };
    return { route: 'delivery', pence: rates.delivery, label: 'Local delivery', free: false };
  }

  /* -------------------------------------------------------------- totals */
  function totals(parts) {
    var lines = [];
    function add(k, pence, note) { lines.push({ key: k, pence: pence, note: note || '' }); }
    add('Carpet', parts.carpetPence, parts.carpetNote);
    if (parts.acc.underlay.pence) add('Underlay ' + parts.acc.underlay.choice, parts.acc.underlay.pence, parts.acc.underlay.m2 + ' m²');
    if (parts.acc.gripper.pence) add('Gripper', parts.acc.gripper.pence, parts.acc.gripper.lengths + ' lengths');
    if (parts.acc.doorBars.pence) add('Door bars', parts.acc.doorBars.pence, parts.acc.doorBars.count + ' bars');
    if (parts.acc.uplift.pence) add('Uplift of your old floor', parts.acc.uplift.pence, fmtM(parts.acc.uplift.m2) + ' m²');
    if (parts.acc.disposal.pence) add('Disposal', parts.acc.disposal.pence, fmtM(parts.acc.disposal.m2) + ' m²');
    if (parts.acc.furniture.pence) add('Furniture moving', parts.acc.furniture.pence, parts.acc.furniture.rooms + ' rooms');
    if (parts.fit.pence) add('Fitting', parts.fit.pence, parts.fit.minApplied ? 'minimum charge applied' : fmtM(parts.fit.m2) + ' m²');
    if (parts.dlv.pence) add(parts.dlv.label, parts.dlv.pence, '');
    var total = 0;
    for (var i = 0; i < lines.length; i++) total += lines[i].pence;
    var deposit = (parts.rates || RATES).deposit;
    return { lines: lines, totalPence: total, depositPence: deposit, balancePence: Math.max(0, total - deposit) };
  }

  /* --------------------------------------------------------- whole quote */
  /* state = { areas:[{kind:'room'|'stairs'|'cut', ...}], underlay, gripper, doorBars,
               uplift, disposal, furnitureRooms, fitting, route }
     roll  = { widthM, units, rollNumber, variantId }
     prod  = { pricePerM2Pence, classification, patternRepeatM } */
  function quote(state, roll, prod, rates) {
    rates = rates || RATES;
    var results = [], blocked = null;
    var areas = state.areas || [];
    for (var i = 0; i < areas.length; i++) {
      var a = areas[i], r;
      if (a.kind === 'stairs') r = stairsCut(a, roll.widthM, prod.classification);
      else if (a.kind === 'cut') r = lengthCut(a, roll.widthM, prod.classification, prod.patternRepeatM);
      else r = roomCut(a, roll.widthM, prod.classification, prod.patternRepeatM);
      if (!r.ok) { blocked = r; break; }
      results.push(r);
    }
    if (blocked) return { ok: false, blocked: true, message: blocked.message, reason: blocked.reason };
    if (!results.length) return { ok: false, blocked: true, reason: 'missing', message: 'Please add at least one room or staircase.' };

    var cutCm = 0, chargeM2 = 0, floorM2 = 0, hasStairs = false, estimate = false, hasCut = false;
    var rooms = [], landings = [], stairCount = 0, stairWidthM = 0, joins = 0;
    for (i = 0; i < results.length; i++) {
      var x = results[i];
      cutCm += x.cutCm;
      chargeM2 += x.chargeableM2;
      floorM2 += x.floorM2;
      if (x.kind === 'stairs') {
        hasStairs = true;
        if (x.estimate) estimate = true;
        stairCount += x.stairCount;
        stairWidthM = Math.max(stairWidthM, x.coverage === 'runner' ? x.finishedWidthM : x.stairWidthM);
        landings = landings.concat(x.landings);
      } else {
        /* A cut to length is priced and gripped exactly like a room: its
           floor area is the whole cut and its perimeter is 2 x (length + width). */
        if (x.kind === 'cut') hasCut = true;
        rooms.push(x);
        if (x.join) joins++;
      }
    }
    chargeM2 = r2(chargeM2); floorM2 = r3(floorM2);
    var carpet = Math.round(chargeM2 * (prod.pricePerM2Pence || 0));

    var acc = accessories({
      rates: rates, floorM2: floorM2, rooms: rooms, landings: landings,
      stairCount: stairCount, stairWidthM: stairWidthM,
      underlay: state.underlay, gripper: state.gripper, doorBars: state.doorBars,
      uplift: state.uplift, disposal: state.disposal, furnitureRooms: state.furnitureRooms
    });
    var fit = fitting(floorM2, hasStairs, state.fitting === 'yes', rates);
    var dlv = delivery(state.route, fit.wanted, rates);
    var tot = totals({
      carpetPence: carpet,
      carpetNote: fmtM(chargeM2) + ' m² cut from a ' + fmtM(roll.widthM) + 'm roll',
      acc: acc, fit: fit, dlv: dlv, rates: rates
    });

    var units = cutCm / 10;
    return {
      ok: true,
      areas: results,
      rollWidthM: roll.widthM,
      totalCutM: toM(cutCm), totalCutCm: cutCm, units: units,
      unitsAvailable: roll.units,
      enoughStock: roll.units == null ? null : (units <= roll.units),
      chargeableM2: chargeM2, floorM2: floorM2,
      carpetPence: carpet, pricePerM2Pence: prod.pricePerM2Pence || 0,
      hasStairs: hasStairs, estimate: estimate, joins: joins, hasCut: hasCut,
      orderIn: prod.orderIn === true,
      accessories: acc, fitting: fit, delivery: dlv,
      lines: tot.lines, totalPence: tot.totalPence,
      depositPence: tot.depositPence, balancePence: tot.balancePence,
      disclaimer: hasStairs ? DISCLAIMER : ''
    };
  }

  /* Compare the same job across every roll width the range has in stock. */
  function compareWidths(state, rolls, prod, rates) {
    var out = [];
    for (var i = 0; i < rolls.length; i++) {
      var q = quote(state, rolls[i], prod, rates);
      out.push({ roll: rolls[i], quote: q });
    }
    var best = null;
    for (i = 0; i < out.length; i++) {
      var o = out[i];
      if (!o.quote.ok) continue;
      if (o.quote.enoughStock === false) continue;
      if (!best) { best = o; continue; }
      if (o.quote.carpetPence < best.quote.carpetPence) best = o;
      else if (o.quote.carpetPence === best.quote.carpetPence && o.quote.joins < best.quote.joins) best = o;
    }
    return { options: out, best: best };
  }

  /* The cutting plan: every cut in the order it comes off the roll, the offcut
     it leaves, and whether that offcut would genuinely cover a later area.
     An offcut only counts when it is wide enough AND long enough with the pile
     running the same way. */
  function cuttingPlan(areas, rollWidthM) {
    var R = cm(rollWidthM), rows = [], offcuts = [], y = 0, i, j;
    for (i = 0; i < areas.length; i++) {
      var a = areas[i];
      var needAcross = a.kind === 'stairs' ? 0 : cm(a.acrossM);
      var needDown = a.kind === 'stairs' ? 0 : cm(a.downM);
      var from = null;
      if (needAcross) {
        for (j = 0; j < offcuts.length; j++) {
          if (!offcuts[j].used && offcuts[j].acrossCm >= needAcross && offcuts[j].downCm >= needDown) {
            from = offcuts[j]; break;
          }
        }
      }
      var offA = a.kind === 'stairs' ? spareOf(a) : { acrossCm: cm(a.offcut.widthM), downCm: cm(a.offcut.lengthM) };
      rows.push({
        index: i + 1,
        name: a.kind === 'stairs' ? 'Stairs' + (a.landings.length ? ' and landing' : '') : a.name,
        kind: a.kind,
        startM: toM(y), cutM: toM(a.cutCm),
        offcutWidthM: toM(offA.acrossCm), offcutLengthM: toM(offA.downCm),
        couldUseOffcutFrom: from ? from.fromName : null,
        offcutNote: (needAcross && !from && offcuts.length)
          ? 'No earlier offcut is wide enough and long enough for this area, so it is cut from fresh carpet.' : ''
      });
      if (offA.acrossCm > 0 && offA.downCm > 0) {
        offcuts.push({ fromName: rows[rows.length - 1].name, acrossCm: offA.acrossCm, downCm: offA.downCm, used: false });
      }
      y += a.cutCm;
    }
    return { rows: rows, totalCutM: toM(y), rollWidthM: toM(R) };
  }

  function spareOf(stairsRes) {
    var best = { acrossCm: 0, downCm: 0 };
    var sh = stairsRes.shelves || [];
    for (var i = 0; i < sh.length; i++) {
      var a = sh[i].spareAcrossCm, d = sh[i].cutCm;
      if (a * d > best.acrossCm * best.downCm) best = { acrossCm: a, downCm: d };
    }
    return best;
  }

  /* Order-in carpet: the range is not on a rack in the shop, so there is no
     roll to reserve. The job is quoted on every width the supplier offers and
     the most economical suitable width is recommended. Ties go to the width
     that needs fewer joins, then to the narrower roll. */
  function orderInOptions(state, widths, prod, rates) {
    var pseudo = [];
    for (var i = 0; i < (widths || []).length; i++) {
      var w = Number(widths[i]);
      if (!isFinite(w) || w <= 0) continue;
      pseudo.push({ widthM: w, units: null, rollNumber: fmtM(w) + 'm width', id: 'orderin-' + w, orderIn: true });
    }
    if (!pseudo.length) {
      return { options: [], best: null, blocked: true,
        message: 'We do not have the roll widths for this carpet on file, so we cannot work the cut out online. Please call the shop and we will quote it by hand.' };
    }
    var p = { pricePerM2Pence: prod.pricePerM2Pence, classification: prod.classification,
              patternRepeatM: prod.patternRepeatM, orderIn: true };
    var out = [];
    for (i = 0; i < pseudo.length; i++) out.push({ roll: pseudo[i], quote: quote(state, pseudo[i], p, rates) });
    var best = null;
    for (i = 0; i < out.length; i++) {
      var o = out[i];
      if (!o.quote.ok) continue;
      if (!best) { best = o; continue; }
      if (o.quote.carpetPence < best.quote.carpetPence) best = o;
      else if (o.quote.carpetPence === best.quote.carpetPence) {
        if (o.quote.joins < best.quote.joins) best = o;
        else if (o.quote.joins === best.quote.joins && o.roll.widthM < best.roll.widthM) best = o;
      }
    }
    return { options: out, best: best, blocked: !best,
      message: best ? '' : 'None of the widths this carpet comes in will cover your rooms without being quoted by hand. Please call the shop and we will work it out with you.' };
  }

  var ORDER_IN_NOTE = 'Ordered in from the factory. Availability confirmed with the factory first. Colours ordered in may carry a small delivery surcharge from the manufacturer, confirmed with you before your order goes ahead.';

  /* ------------------------------------------------- where a roll physically is */
  /* The customer is told the site in these exact words and never sees a
     position code, a batch, a cost or an internal note. */
  var SITE_WORDING = {
    'Showroom rack': 'Available to view in our Clydebank showroom.',
    'Warehouse 1': 'Available from our superstore. Reserve online or contact us to arrange viewing.',
    'Warehouse 2': 'Available from our superstore. Reserve online or contact us to arrange viewing.',
    'Remnant area': 'Remnant piece. Available to view in our Clydebank showroom.'
  };
  function siteWording(site) { return SITE_WORDING[site] || ''; }
  function siteGroup(site) {
    if (site === 'Warehouse 1' || site === 'Warehouse 2') return 'Superstore';
    if (site === 'Remnant area') return 'Remnant';
    if (site === 'Showroom rack') return 'Showroom';
    return '';
  }

  /* A roll reaches the builder only when staff have said it may. A blank
     status counts as Available for now, as agreed. "Remnant" is sellable too:
     a remnant is a real piece we advertise on the remnants page, so it has to
     be quotable in the builder as well. Its length still has to cover the job,
     and sell online still has to be ticked. */
  function rollSellable(roll) {
    if (!roll) return false;
    if (roll.sellOnline === false) return false;
    var st = roll.status == null ? '' : String(roll.status);
    return st === '' || st === 'Available' || st === 'Remnant';
  }

  /* Which roll to recommend. In order: the cut has to work on that width, the
     roll has to be long enough, then the least carpet used, then the SHORTEST
     roll that still fits, so short rolls are used up and full rolls are left
     whole. Only ever returns one roll: a job is never split across two, and two
     batches are never mixed.
     widthM (optional, round 5): when the customer has picked a roll width,
     only rolls of exactly that width are considered at all. */
  function chooseRoll(state, rolls, prod, rates, widthM) {
    var out = [], i;
    var wantW = Number(widthM) > 0 ? cm(widthM) : 0;
    for (i = 0; i < (rolls || []).length; i++) {
      var r = rolls[i];
      if (wantW && cm(r.widthM) !== wantW) continue;
      var q = quote(state, r, prod, rates);
      out.push({ roll: r, quote: q, usable: !!(q.ok && q.enoughStock !== false) });
    }
    var fits = [];
    for (i = 0; i < out.length; i++) if (out[i].usable) fits.push(out[i]);
    fits.sort(function (a, b) {
      if (a.quote.chargeableM2 !== b.quote.chargeableM2) return a.quote.chargeableM2 - b.quote.chargeableM2;
      if (a.quote.joins !== b.quote.joins) return a.quote.joins - b.quote.joins;
      var au = a.roll.units == null ? Infinity : a.roll.units;
      var bu = b.roll.units == null ? Infinity : b.roll.units;
      if (au !== bu) return au - bu;
      return String(a.roll.rollNumber).localeCompare(String(b.roll.rollNumber));
    });
    return { options: out, best: fits.length ? fits[0] : null };
  }


  function rollStatus(units, originalLengthM) {
    if (!units || units <= 0) return { key: 'sold', label: 'Sold out' };
    if (units < 40) return { key: 'remnant', label: 'Remnant' };
    if (units < 100) return { key: 'short', label: 'Short roll' };
    return { key: 'full', label: 'Full roll' };
  }

  return {
    RATES: RATES, TRIM_CM: TRIM, STAIR_ALLOW_CM: STAIR_ALLOW, GRIP_CM: GRIP, DOORWAY_CM: DOORWAY,
    DISCLAIMER: DISCLAIMER,
    cm: cm, toM: toM, up10: up10, m2: m2, r2: r2, r3: r3, fmtM: fmtM, fmtW: fmtW, toMetres: toMetres,
    isPlain: isPlain, isPatterned: isPatterned, canTurn: canTurn,
    roomGeom: roomGeom, roomCut: roomCut,
    CUT_MIN_CM: CUT_MIN, lengthCut: lengthCut,
    stairsCut: stairsCut, stairsPieces: stairsPieces, landingPieces: landingPieces,
    layout: layout, widthLabel: widthLabel, cuttingPlan: cuttingPlan,
    accessories: accessories, fitting: fitting, delivery: delivery, totals: totals,
    quote: quote, compareWidths: compareWidths, orderInOptions: orderInOptions,
    ORDER_IN_NOTE: ORDER_IN_NOTE, rollStatus: rollStatus,
    siteWording: siteWording, siteGroup: siteGroup, rollSellable: rollSellable, chooseRoll: chooseRoll
  };
}));
